/**
 * GPS WebSocket Event Manager
 * Handles event subscription and emission with type safety
 */

import type { GpsWsEvent, GpsWsEventHandler } from './types';

/**
 * Event Manager class
 * Separated event handling logic for better organization
 */
export class EventManager {
  private eventHandlers: Partial<Record<GpsWsEvent, GpsWsEventHandler[]>> = {};

  /**
   * Add event listener
   */
  on<T = any>(event: GpsWsEvent, handler: GpsWsEventHandler<T>): void {
    if (!this.eventHandlers[event]) {
      this.eventHandlers[event] = [];
    }
    this.eventHandlers[event]!.push(handler as GpsWsEventHandler);
  }

  /**
   * Remove specific event listener
   */
  off(event: GpsWsEvent, handler: GpsWsEventHandler): void {
    const handlers = this.eventHandlers[event];
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index !== -1) {
        handlers.splice(index, 1);
      }
    }
  }

  /**
   * Remove all listeners for a specific event
   */
  removeAllListeners(event?: GpsWsEvent): void {
    if (event) {
      this.eventHandlers[event] = [];
    } else {
      this.eventHandlers = {};
    }
  }

  /**
   * Emit event to all registered handlers
   */
  emit<T = any>(event: GpsWsEvent, data?: T): void {
    const handlers = this.eventHandlers[event] || [];
    
    handlers.forEach((handler) => {
      try {
        handler(data);
      } catch (error) {
        // Silently handle handler errors to prevent disrupting other handlers
        if (typeof console !== 'undefined') {
          console.error(`Error in GPS WebSocket event handler for ${event}:`, error);
        }
      }
    });
  }

  /**
   * Get number of handlers for an event
   */
  getHandlerCount(event: GpsWsEvent): number {
    return this.eventHandlers[event]?.length || 0;
  }

  /**
   * Get all registered events
   */
  getRegisteredEvents(): GpsWsEvent[] {
    return Object.keys(this.eventHandlers) as GpsWsEvent[];
  }

  /**
   * Check if event has any handlers
   */
  hasHandlers(event: GpsWsEvent): boolean {
    return this.getHandlerCount(event) > 0;
  }

  /**
   * Clear all event handlers
   */
  destroy(): void {
    this.eventHandlers = {};
  }
}